import { useMemo, type ReactNode } from "react";
import { fromPublicProfile, type StorySectionKey } from "@/lib/orgStory";
import type { PublicOrganizationProfile } from "@/lib/publicOrganizationProfile";
import LookingForCard from "@/components/organization/LookingForCard";
import SignUpPrompt from "@/components/auth/SignUpPrompt";
import { GATED_INSIGHTS_LABEL } from "@/components/auth/signUpPromptCopy";
import OrgStory from "./OrgStory";
import { buildToolStack } from "./storyData";
import StoryHeader from "./StoryHeader";
import { DecisionPanel, MobileDecisionBar, NO_FIT, PUBLIC_SIGN_IN_HREF, StoryHeroActions } from "./DecisionPanel";
import PublicNewsSection from "./PublicNewsSection";
import { usePublicRegister, usePublicSignIn } from "./publicSignIn";
import { officesDisplayFor } from "@/lib/workModel";
import {
  OverviewSection,
  OfferSection,
  GrowthSection,
  OperationsSection,
  FootprintSection,
  storySectionHasContent,
} from "./sections";

/**
 * The org story page for a signed-out visitor and for the prerender (spec
 * 2026-09-24 §5, §6.4, §6.5): the anonymous RPC through `fromPublicProfile`,
 * laid out by `OrgStory`.
 *
 * PRERENDER CONTRACT. scripts/prerender.mjs renders this with renderToString
 * under Node, mounted through src/entry-prerender.tsx (which does provide a
 * router and a TooltipProvider) with the prerender data around it; the
 * script itself stubs `localStorage` because the Supabase client reads it at
 * module load. The rule this component tree must hold is narrower than "no
 * providers": no auth hook (useAuth, useAgentOrgActions) and no
 * window/document/localStorage read *during render* — only in effects and
 * handlers. PublicOrganizationProfile.story.ssr.test.tsx mocks both
 * `@/hooks/useAuth` and `@/hooks/useAgentOrgActions` with spies and asserts
 * neither is called while rendering this tree, so a future section that
 * reaches for either hook fails that test rather than silently crashing the
 * next prerender build.
 *
 * Team and Reviews never render here (storySections excludes them for the
 * public audience) and there is no discovery call. The public RPC carries
 * tools with their category already resolved (#958), so buildToolStack groups
 * them from profileData with no catalog query for anonymous visitors.
 */
export default function PublicOrgStory({
  profile,
  topSlot,
}: {
  profile: PublicOrganizationProfile;
  topSlot?: ReactNode;
}) {
  const view = useMemo(() => fromPublicProfile(profile), [profile]);
  // Inside the page's PublicSignInProvider every account action opens one
  // sign-in box; without one (the flag-off page never renders this) they
  // fall back to the sign-in link.
  const onSignIn = usePublicSignIn() ?? undefined;
  const onRegister = usePublicRegister() ?? undefined;
  // "Start a conversation" opens sign-up with the message prompt (founder
  // decision F6); the sign-up view keeps "Already have an account? Sign In".
  const onStartConversation = onRegister ? () => onRegister("message") : undefined;
  const stack = useMemo(() => buildToolStack(view.profileData, []), [view.profileData]);
  const sections = view.sections.filter((key) => storySectionHasContent(key, view, { stack }));

  const lookingFor = (
    <LookingForCard
      lookingFor={view.profileData.lookingFor}
      offices={view.officeLabels}
      officesDisplay={officesDisplayFor(view.workModel, view.profileData.officesDisplay)}
      orgId={view.identity.orgId}
      viewer="signedOut"
      signInHref={PUBLIC_SIGN_IN_HREF}
      // The card's only use of this is its "Start a conversation" button, which
      // opens sign-up with the message prompt like the panel's (founder decision F6).
      onSignIn={onStartConversation ?? onSignIn}
    />
  );

  // Compare needs an account (founder decision F6). The looking-for card sits
  // in the hero here, so the panel's below-the-glance slot carries this prompt.

  const comparePrompt = <SignUpPrompt unlock="compare" onSignUp={onRegister} className="sm:flex-col sm:items-start" />;

  const renderSection = (key: StorySectionKey) => {
    switch (key) {
      case "overview":
        // The RPC returns published organizations only.
        return <OverviewSection view={view} isPublished />;
      case "offer":
        // Founder decision F6: compensation model, fees and leads need an account.
        return (
          <OfferSection
            view={view}
            insightsTeaser={<SignUpPrompt unlock="compensation" label={GATED_INSIGHTS_LABEL} onSignUp={onRegister} />}
          />
        );
      case "growth":
        return <GrowthSection view={view} />;
      case "operations":
        return <OperationsSection view={view} stack={stack} />;
      case "footprint":
        return <FootprintSection view={view} />;
      case "news":
        return <PublicNewsSection view={view} />;
      default:
        // team, reviews: never public.
        return null;
    }
  };

  return (
    <OrgStory
      view={view}
      topSlot={topSlot}
      header={
        <StoryHeader
          view={view}
          titleAs="h1"
          actions={<StoryHeroActions view={view} variant="public" />}
          aside={lookingFor}
        />
      }
      panel={
        <DecisionPanel
          view={view}
          fit={NO_FIT}
          variant="public"
          showShare={false}
          lookingFor={comparePrompt}
          onSignIn={onSignIn}
          onRegister={onRegister}
          onStartConversation={onStartConversation}
          showWebsite={false}
        />
      }
      mobileAside={
        <DecisionPanel
          view={view}
          fit={NO_FIT}
          variant="public"
          showPrimary={false}
          showShare={false}
          lookingFor={comparePrompt}
          onSignIn={onSignIn}
          onRegister={onRegister}
          showWebsite={false}
        />
      }
      mobileBar={
        <MobileDecisionBar
          view={view}
          fit={NO_FIT}
          signInHref={PUBLIC_SIGN_IN_HREF}
          onSignIn={onSignIn}
          onStartConversation={onStartConversation}
        />
      }
      sections={sections}
      renderSection={renderSection}
    />
  );
}
